import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../services/api';
import './ProjectForm.css';

const ProjectForm = ({ project, houseId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    status: 'planned',
    priority: 'medium',
    budget_estimated: '',
    start_date: '',
    target_date: '',
    completion_date: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        category: project.category || '',
        status: project.status || 'planned',
        priority: project.priority || 'medium',
        budget_estimated: project.budget_estimated || '',
        start_date: project.start_date || '',
        target_date: project.target_date || '',
        completion_date: project.completion_date || '',
      });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        house_id: houseId || null,
        budget_estimated: formData.budget_estimated ? parseFloat(formData.budget_estimated) : null,
        start_date: formData.start_date || null,
        target_date: formData.target_date || null,
        completion_date: formData.completion_date || null,
        description: formData.description || null,
        category: formData.category || null,
      };

      if (project) {
        await projectsAPI.update(project.id, data);
      } else {
        await projectsAPI.create(data);
      }

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="project-form-overlay">
      <div className="project-form-modal">
        <div className="project-form-header">
          <h2>{project ? 'Edit Project' : 'Create New Project'}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-group">
            <label htmlFor="name">Project Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Kitchen Renovation"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Describe your project..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                <option value="Kitchen">Kitchen</option>
                <option value="Bathroom">Bathroom</option>
                <option value="Bedroom">Bedroom</option>
                <option value="Living Room">Living Room</option>
                <option value="Garden">Garden</option>
                <option value="Exterior">Exterior</option>
                <option value="Basement">Basement</option>
                <option value="Attic">Attic</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="planned">Planned</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority *</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="budget_estimated">Estimated Budget ($)</label>
            <input
              type="number"
              id="budget_estimated"
              name="budget_estimated"
              value={formData.budget_estimated}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_date">Start Date</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="target_date">Target Date</label>
              <input
                type="date"
                id="target_date"
                name="target_date"
                value={formData.target_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="completion_date">Completion Date</label>
              <input
                type="date"
                id="completion_date"
                name="completion_date"
                value={formData.completion_date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : (project ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
